<template>
  <div class="QuestionComponent">
    <h3 class="no-transform">
      Q{{ index }}. {{ question.question }}
    </h3>
    <v-row v-if="question.question_documents">
      <v-col
        v-for="(document, key) in question.question_documents"
        :key="key"
        cols="12"
        sm="6"
        md="4"
      >
        <v-btn
          color="primary"
          text
          link
          :href="'/file/' + document.id"
          target="_blank"
        >
          <v-icon left>
            mdi-file-document
          </v-icon>{{ document.name }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="checkTypeName(question.question_type.name)">
      <v-col cols="12">
        <v-text-field
          id="id"
          v-model="answer"
          :name="question.question"
          :placeholder="question.placeholder"
          :type="question.question_type.name.toLowerCase()"
          :rules="getRules(question.rules)"
        />
      </v-col>
    </v-row>
    <v-row v-else-if="question.question_type.name === 'Text Modal'">
      <v-col
        cols="12"
        sm="6"
      >
        <v-dialog
          v-model="modal"
          content-class="vdialog-question-comp"
          attach
        >
          <template v-slot:activator="{ on, attrs }">
            <div>
              <small>{{ question.placeholder }}</small>
            </div>

            <v-btn
              color="primary"
              dark
              v-bind="attrs"
              v-on="on"
              @click="updateTextModal"
            >
              Read
            </v-btn>
          </template>
          <v-card>
            <v-card-title />
            <v-card-text
              style="line-height: 1.5"
              v-html="question.html"
            />
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                text
                @click="modal = false"
              >
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
    <v-row v-else-if="question.question_type.name === 'Signature' && currentIndex >= sectionIndex">
      <v-col cols="12">
        <div>
          <small>{{ question.placeholder }}</small>
          <signature-component
            ref="sig"
            :loading="loading"
            :disabled="disabled && currentIndex >= sectionIndex"
            @signatureSaved="uploadSignature($event)"
          />
        </div>
      </v-col>
    </v-row>
    <v-row v-else-if="question.question_type.name === 'Signature (Combined)' && currentIndex >= sectionIndex">
      <v-col cols="12">
        <div>
          <small>{{ question.placeholder }}</small>
          <signature-component
            ref="sig"
            :loading="loading"
            :disabled="disabled && currentIndex >= sectionIndex"
            @signatureSaved="uploadSignature($event)"
          />
        </div>
      </v-col>
      <v-col
        cols="12"
        sm="6"
      >
        <v-text-field
          id="id"
          v-model="answer.full_name"
          :name="question.question"
          placeholder="Print Full Name"
          :type="question.question_type.name.toLowerCase()"
          :rules="[...Rules.required]"
        />
      </v-col>
      <v-col
        cols="12"
        sm="6"
      >
        <v-dialog
          ref="dialog"
          v-model="modal"
          :return-value.sync="answer.date"
          persistent
          width="290px"
          attach
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="answer.date"
              label="Date"
              append-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              :rules="[...Rules.required]"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="date"
            scrollable
            :max="maxDate"
          >
            <v-spacer />
            <v-btn
              text
              color="primary"
              @click="modal = false"
            >
              Cancel
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="$refs.dialog.save(date)"
            >
              OK
            </v-btn>
          </v-date-picker>
        </v-dialog>
      </v-col>
    </v-row>
    <v-row v-else-if="question.question_type.name === 'Date Picker'">
      <v-col cols="12">
        <v-dialog
          ref="dialog"
          v-model="modal"
          :return-value.sync="answer"
          persistent
          width="290px"
          attach
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="answer"
              :label="question.placeholder"
              append-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              :rules="getRules(question.rules)"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="date"
            scrollable
            :max="maxDate"
          >
            <v-spacer />
            <v-btn
              text
              color="primary"
              @click="modal = false"
            >
              Cancel
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="$refs.dialog.save(date)"
            >
              OK
            </v-btn>
          </v-date-picker>
        </v-dialog>
      </v-col>
    </v-row>
    <v-row v-else-if="question.question_type.name === 'Dropdown'">
      <v-col
        cols="12"
        sm="6"
      >
        <v-autocomplete
          v-model="answer"
          :items="question.question_options"
          placeholder="Start typing to Search"
          item-text="name"
          item-value="name"
        />
      </v-col>
    </v-row>
    <v-row v-else-if="question.question_type.name === 'Checkboxes'">
      <v-col
        v-for="(option, optionKey) in question.question_options"
        :key="optionKey"
        cols="12"
        sm="6"
        md="4"
      >
        <v-checkbox
          v-model="answer"
          :label="option.name"
          :value="option.name"
          :rules="checkbox(question.rules)"
          :multiple="true"
        />
      </v-col>
    </v-row>
    <v-radio-group
      v-else-if="question.question_type.name === 'Radio Buttons'"
      v-model="answer"
      mandatory
    >
      <v-row>
        <v-col
          v-for="(option, key) in question.question_options"
          :key="key"
          cols="12"
          sm="6"
          md="4"
        >
          <v-radio
            :label="option.name"
            :value="option.name"
          />
        </v-col>
      </v-row>
    </v-radio-group>
  </div>
</template>
<script>
export default {
  name: 'QuestionComponent',
  props: {
    question: {
      type: Object,
      default: () => {
        return {}
      }
    },
    index: {
      type: Number,
      default: 1
    },
    currentIndex: {
      type: Number,
      required: true
    },
    sectionIndex: {
      type: Number,
      required: true
    }
  },
  data: () => ({
    loading: false,
    disabled: false,
    maxDate: new Date(new Date().setFullYear(new Date().getFullYear() + 10)).toISOString().substr(0, 10),
    answer: null,
    modal: null,
    date: new Date().toISOString().substr(0, 10),
    initalDraw: true
  }),
  computed: {
    storageRoot () {
      return process.env.MIX_STORAGE_URL
    }
  },
  watch: {
    answer: {
      deep: true,
      handler (newQuestion, oldQuestion) {
        this.$emit('answering', newQuestion)
      }
    },
    currentIndex: {
      deep: true,
      handler () {
        if (this.currentIndex < this.sectionIndex) {
          this.disabled = false
        }
      }
    }
  },
  created () {
    if (this.question.question_type.needs_options) {
      this.answer = []
    }
    if (this.question.question_type.name === 'Signature (Combined)') {
      this.answer = {}
    }
    if (this.question.question_type.name === 'Text Modal') {
      this.answer = 'unread'
    }
  },
  methods: {
    getRules (rules) {
      var questionRules = []
      if (rules) {
        rules.forEach(rule => {
          questionRules.push(this.getFunction(rule))
        })
      }
      return questionRules
    },
    getFunction (str) {
      switch (str) {
        case 'required':
          return this.Rules.requiredCandidate

        case 'accepted':
          return this.Rules.accepted

        case 'email':
          return this.Rules.email

        case 'phone':
          return this.Rules.phone

        case 'number':
          return this.Rules.numeric

        case 'text':
          return this.Rules.alphaNumericDashSpace

        case 'date':
          return this.Rules.date

        default:
          return true
      }
    },
    multiple (rules) {
      return _.indexOf(rules, 'multiple') !== -1
    },
    checkbox (rules) {
      return _.indexOf(rules, 'required') !== -1 ? [
        this.answer === null || this.answer.length > 0 || 'At least one item should be selected'
      ] : []
    },
    checkTypeName (name) {
      return _.includes(['Number', 'Text', 'Phone', 'Email'], name)
    },
    uploadSignature (image) {
      const self = this
      self.loading = true
      const formData = new FormData()
      formData.append('file', image)
      formData.append('first_name', self.$store.state.candidates.candidate.first_name)
      formData.append('last_name', self.$store.state.candidates.candidate.last_name)
      axios.post(self.URLs.core.candidates.candidateSignature, formData)
        .then(response => {
          if (typeof self.answer === 'object' && self.answer !== null) {
            self.answer.url = response.data
          } else {
            self.answer = response.data
          }
          self.loading = false
          self.disabled = true
        })
    },
    redrawIfFirst () {
      if (this.initalDraw) {
        this.$refs.sig.redraw()
        this.initalDraw = false
      }
    },
    updateTextModal () {
      this.answer = 'read'
    }
  }
}
</script>
<style lang="scss">
.QuestionComponent {
    margin: 1rem 0;
    padding: 0 28px;
}

.vdialog-question-comp table {
    border: 1px solid;
    width: 100%;
}
.vdialog-question-comp th, .vdialog-question-comp td {
  border: 1px solid;
}
</style>
